import { useEffect } from 'react'
import { createFetchActionCreators, createFetchReducer } from '@navinc/utils'
import { useToast } from '@navinc/base-react-components'
import { selectors } from './selectors'
import { usePartner } from '../../../hooks/apollo/use-partner'
import { getToken } from '../../../../utils/tokens'
import { createDomain } from './create-domain'
import { request } from '../../../utils/request'

const BILLING_DETAILS_LOAD = 'BILLING_DETAILS_LOAD'
const BILLING_DETAILS_FAILURE = 'BILLING_DETAILS_FAILURE'
const BILLING_DETAILS_SUCCESS = 'BILLING_DETAILS_SUCCESS'

const [Provider, useDomain] = createDomain({
  actions: createFetchActionCreators({
    failureActionType: BILLING_DETAILS_FAILURE,
    loadActionType: BILLING_DETAILS_LOAD,
    successActionType: BILLING_DETAILS_SUCCESS,
  }),
  domain: 'billing-details',
  reducer: createFetchReducer({
    failureActionType: BILLING_DETAILS_FAILURE,
    loadActionType: BILLING_DETAILS_LOAD,
    successActionType: BILLING_DETAILS_SUCCESS,
  }),
  selectors,
  initialState: { data: {}, error: null, isLoading: false },
})

const fetchDetails =
  ({ loadActionCreator, successActionCreator, failureActionCreator }) =>
  () => {
    loadActionCreator()

    return request({
      url: `/ent-api/api/authentication/billing/details`,
      headers: {
        authorization: `Token ${getToken()}`,
      },
    })
      .then(({ data }) => {
        successActionCreator(data)
        return data
      })
      .catch((error) => {
        failureActionCreator(error)
        return Promise.reject(error)
      })
  }

export const useBillingDetails = () => {
  const domain = useDomain()
  const { failureActionCreator, getHasAFetchFinished, getIsLoading, loadActionCreator, successActionCreator } = domain
  const { showMessage } = useToast()

  const { getFormattedCustomerSupportPhone } = usePartner()
  const customerSupportPhone = getFormattedCustomerSupportPhone()

  const hasAFetchFinished = getHasAFetchFinished()
  const isLoading = getIsLoading()
  const getDetails = fetchDetails({ loadActionCreator, successActionCreator, failureActionCreator })

  useEffect(() => {
    if (!hasAFetchFinished && !isLoading) {
      getDetails().catch(() =>
        showMessage({
          copy: `Please call our customer support team at ${customerSupportPhone}, 7am - 5pm MST, Monday - Friday.`,
          shouldNotTimeout: true,
          title: 'Something went wrong fetching your billing info.',
          type: 'error',
        })
      )
    }
  }, [customerSupportPhone, getDetails, hasAFetchFinished, isLoading, showMessage])

  return {
    getDetails,
    ...domain,
  }
}

export { Provider as BillingDetailsProvider }
